import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = article
    ? getRelatedArticles(article.id, article.category, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishDate,
          author: { '@type': 'Organization', name: article.author },
          publisher: { '@type': 'Organization', name: '每日大赛' },
          url: `https://dairise.github.io/article/${article.id}`,
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                {article.featured && (
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    精选
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>
            </div>

            <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 每日大赛`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-12
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:mt-10 prose-headings:mb-4
                prose-h3:text-xl prose-h3:font-semibold
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="flex flex-wrap gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="border-t border-background-200/60 pt-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">
                  分享文章
                </h3>
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-foreground-600"></i>
                  </button>
                  <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link text-foreground-600"></i>
                  </button>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200/60 pt-10 pb-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block bg-background-50 rounded-xl border border-background-200/60 overflow-hidden hover:border-background-300 transition-all cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={`${rel.title} - 每日大赛`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h4>
                        <span className="text-xs text-foreground-400 mt-1.5 block">{rel.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}